"use client";

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { HomePageSchema } from '@/components/seo/SchemaMarkup';
import { SoftwareApplicationSchema } from '@/components/seo/SoftwareApplicationSchema';
import SEOLinks from '@/components/seo/SEOLinks';
import { trackCTAClick, trackFeatureExploration, trackDemoRequest } from '@/lib/analytics';
import { LatestBlogPosts } from '@/components/blog/LatestBlogPosts';
import siteConfig from '@/config/siteConfig';
import AppLayout from "@/components/layout/AppLayout";

export default function HomePageClient() {

  const handleScheduleDemoClick = () => {
    trackCTAClick('Schedule a Demo', 'Hero Section');
    trackDemoRequest();
  };

  const handleExplorePlatformClick = () => {
    trackCTAClick('Explore Platform', 'Hero Section');
    trackFeatureExploration('Platform Overview', 'Hero Section');
  };

  const handleLearnMoreClick = (featureName: string) => {
    trackFeatureExploration(featureName, 'Feature Cards');
  };

  return (
    <>
      <meta name="author" content={siteConfig.authors} />
      <meta property="article:published_time" content={siteConfig.publishedTime} />
      <meta property="fb:app_id" content={siteConfig.FacebookAppID} />
      <HomePageSchema />
      <SoftwareApplicationSchema />
      <SEOLinks customPath="/" />
      <AppLayout>
        {/* Hero Section */}
        <section className="gradient-bg text-white">
          <div className="container py-16 md:py-24">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h1 className="text-white mb-6">
                  Unlock Your Growth: AI-Powered Commerce, Effortlessly Scaled.
                </h1>
                <p className="text-lg mb-8 text-gray-100">
                  Build stores, marketplaces, and loyalty programs on one platform. Get smart insights that help you understand customers and grow faster. Built for the Agentic Web.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link href="/contact/schedule-demo" className="btn-cta inline-block text-center" onClick={handleScheduleDemoClick}>
                    Schedule a Demo
                  </Link>
                  <Link href="/platform/overview" className="btn-secondary bg-transparent border-white text-white hover:bg-white/10 inline-block text-center" onClick={handleExplorePlatformClick}>
                    Explore Platform
                  </Link>
                </div>
              </div>
              <div className="hidden md:block">
                <Image
                  src="/images/hero/platform-visualization.jpg"
                  alt="Platform Visualization"
                  width={600}
                  height={316}
                  priority={true}
                  fetchPriority="high"
                  loading="eager"
                  className="rounded-lg"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
              </div>
            </div>
          </div>
        </section>
        
        {/* Platform Overview Section */}
        <section className="section bg-white">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="mb-4">
                The Smart Commerce Platform for Forward-Thinking Businesses
              </h2>
              <p className="max-w-3xl mx-auto text-lg">
                Build your commerce business with our AI-native commerce cloud platform. Get smart insights that help you understand customers and make better decisions.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* E-commerce Templates */}
              <div className="card">
                <div className="w-12 h-12 bg-[#E6F7F9] rounded-full flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#00A4BD]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <h3 className="text-xl mb-2">
                  E-commerce Module
                </h3>
                <p className="mb-4">
                  Build your e-commerce with flexible modules and leverage AI-powered recommendations to boost sales.
                </p>
                <Link href="/platform/e-commerce-module" className="text-[#00A4BD] font-medium hover:text-[#008CA1]" onClick={() => handleLearnMoreClick('E-commerce Module')}>
                  Learn More →
                </Link>
              </div>

              {/* Marketplace Module */}
              <div className="card">
                <div className="w-12 h-12 bg-[#E6F7F9] rounded-full flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#00A4BD]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                  </svg>
                </div>
                <h3 className="text-xl mb-2">
                  Marketplace Module
                </h3>
                <p className="mb-4">
                  Create smart peer-to-peer marketplaces with AI-powered product listing & pricing, and secure commission handling.
                </p>
                <Link href="/platform/marketplace-module" className="text-[#00A4BD] font-medium hover:text-[#008CA1]" onClick={() => handleLearnMoreClick('Marketplace Module')}>
                  Learn More →
                </Link>
              </div>

              {/* Analytics & Insights */}
              <div className="card">
                <div className="w-12 h-12 bg-[#E6F7F9] rounded-full flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#00A4BD]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-xl mb-2">
                  Analytics & Insights
                </h3>
                <p className="mb-4">
                  Get smart insights that go beyond analytics - understand customer behavior and predict trends to grow faster.
                </p>
                <Link href="/platform/analytics-insights" className="text-[#00A4BD] font-medium hover:text-[#008CA1]" onClick={() => handleLearnMoreClick('Analytics & Insights')}>
                  Learn More →
                </Link>
              </div>

              {/* Loyalty Programs */}
              <div className="card">
                <div className="w-12 h-12 bg-[#E6F7F9] rounded-full flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#00A4BD]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                </div>
                <h3 className="text-xl mb-2">
                  Loyalty Programs
                </h3>
                <p className="mb-4">
                  Build smart, personalized loyalty programs with tokenized rewards that create lasting customer relationships.
                </p>
                <Link href="/platform/loyalty-rewards" className="text-[#00A4BD] font-medium hover:text-[#008CA1]" onClick={() => handleLearnMoreClick('Loyalty Programs')}>
                  Learn More →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Case Study Highlight Section */}
        <section className="section bg-gray-50">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="mb-4">
                Showcase Marketplace Built on Our Commerce Cloud Platform
              </h2>
              <p className="max-w-3xl mx-auto text-lg">
                Discover BooksShark, a marketplace built by Perxify to demonstrate the power and versatility of our commerce cloud platform in a real-world application.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="hidden md:block">
                  <div className="bg-white/10 rounded-lg h-80 flex items-center justify-center">
                    <Image
                      src="/images/features/booksshark-screenshot.jpg"
                      alt="BooksShark Screenshot"
                      width={600}
                      height={458}
                      priority={true}
                      fetchPriority="high"
                      loading="eager"
                      className="rounded-lg"
                      sizes="(max-width: 768px) 100vw, 50vw"
                    />
                  </div>
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <h3 className="text-2xl mb-4">
                    BooksShark: A Perxify Showcase Marketplace
                  </h3>
                  <p className="mb-4">
                  Explore BooksShark, a fully functional secondhand book and board game marketplace built by the Perxify team to demonstrate our platform's capabilities, featuring AI-powered smart search and listing tools.
                </p>
                  <Link href="/case-studies/booksshark" className="btn-primary inline-block self-start" onClick={() => handleLearnMoreClick('BooksShark')}>
                  Explore BooksShark
                  </Link>
                </div>
              </div>
            </div>

            {/* <div className="mt-8 text-center">
              <Link href="/case-studies" className="btn-secondary inline-block">
                View All Case Studies
              </Link>
            </div> */}
          </div>
        </section>

        <LatestBlogPosts />

        {/* CTA Section */}
        <section className="gradient-bg text-white">
          <div className="container py-16 text-center">
            <h2 className="text-white mb-6">
              Ready to Transform Your Commerce Experience?
            </h2>
            <p className="text-lg mb-8 text-gray-100 max-w-3xl mx-auto">
              Join businesses using Perxify's commerce cloud platform to build powerful e-commerce stores, marketplaces, and loyalty programs that drive growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/contact/schedule-demo" className="btn-cta inline-block" onClick={handleScheduleDemoClick}>
                Schedule a Demo
              </Link>
              <Link href="/platform/overview" className="btn-secondary bg-transparent border-white text-white hover:bg-white/10 inline-block" onClick={handleExplorePlatformClick}>
                Explore Platform
              </Link>
            </div>
          </div>
        </section>
      </AppLayout>
    </>
  );
}
